<template>
  <div class="wb-card">
    <!-- Owner item 2.1: the restricted welcome balance as the backend sums it
         up (welcome_bonus_summary): what was granted, what covered company
         commission, what is left, and the closure workflow of the accountant's
         spec (the finance manager closes or requests, a second user approves
         above the materiality threshold). -->
    <template v-if="summary">
      <div class="wb-head">
        <span class="wb-title">{{ $t("captainProfilePage.welcomeBonus.title") }}</span>
        <v-chip small text-color="white" :color="statusColor">{{ statusLabel }}</v-chip>
        <v-spacer></v-spacer>
        <v-btn v-if="canClose" small outline color="error" :disabled="busy" @click="openCloseDialog">
          {{ $t("captainProfilePage.welcomeBonus.closeButton") }}
        </v-btn>
      </div>

      <div class="wb-figures">
        <div class="wb-figure">
          <span class="wb-label">{{ $t("captainProfilePage.welcomeBonus.granted") }}</span>
          <span class="wb-value">{{ formatMoney(summary.granted) }}</span>
        </div>
        <div class="wb-figure">
          <span class="wb-label">{{ $t("captainProfilePage.welcomeBonus.consumed") }}</span>
          <span class="wb-value">{{ formatMoney(summary.consumed) }}</span>
        </div>
        <div class="wb-figure">
          <span class="wb-label">{{ $t("captainProfilePage.welcomeBonus.remaining") }}</span>
          <span class="wb-value remaining">{{ formatMoney(summary.remaining) }}</span>
        </div>
        <div class="wb-figure" v-if="grantedAt">
          <span class="wb-label">{{ $t("captainProfilePage.welcomeBonus.grantedAt") }}</span>
          <span class="wb-value">{{ formatDay(grantedAt) }}</span>
        </div>
        <div class="wb-figure">
          <span class="wb-label">{{ $t("captainProfilePage.welcomeBonus.expiresAt") }}</span>
          <span class="wb-value">
            {{ summary.expires_at ? formatDay(summary.expires_at) : $t("captainProfilePage.welcomeBonus.neverExpires") }}
          </span>
        </div>
        <div class="wb-figure" v-if="summary.closed_at">
          <span class="wb-label">{{ $t("captainProfilePage.welcomeBonus.closedAt") }}</span>
          <span class="wb-value">{{ formatDateTime(summary.closed_at) }}</span>
        </div>
      </div>
      <div class="wb-note">{{ $t("captainProfilePage.welcomeBonus.restrictedNote") }}</div>

      <!-- A closure above the materiality threshold waits here for a second
           user; the backend refuses the requester himself and says so. -->
      <div v-if="pending" class="wb-pending">
        <div class="wb-pending-title">{{ $t("captainProfilePage.welcomeBonus.pendingTitle") }}</div>
        <div>
          {{
            $t("captainProfilePage.welcomeBonus.pendingDetail", {
              kind: kindLabel(pending.kind),
              amount: formatMoney(pending.amount),
              name: pending.requested_by_name || "-",
              date: formatDateTime(pending.requested_at),
            })
          }}
        </div>
        <div v-if="pending.reason" class="wb-reason">
          {{ $t("captainProfilePage.welcomeBonus.reason") }}: {{ pending.reason }}
        </div>
        <div class="wb-pending-actions">
          <v-btn v-if="canApprove" small color="success" :loading="approving" :disabled="busy" @click="approveClose">
            {{ $t("captainProfilePage.welcomeBonus.approve") }}
          </v-btn>
          <v-btn v-if="canCancel" small flat :loading="cancelling" :disabled="busy" @click="cancelClose">
            {{ $t("captainProfilePage.welcomeBonus.cancelRequest") }}
          </v-btn>
        </div>
      </div>

      <!-- The audit trail, loaded the first time the panel is opened. -->
      <v-expansion-panel v-model="movementsPanel" class="wb-movements">
        <v-expansion-panel-content lazy>
          <div slot="header">{{ $t("captainProfilePage.welcomeBonus.movements") }}</div>
          <v-data-table
            :headers="movementHeaders"
            :items="movements"
            :loading="movementsLoading"
            :rows-per-page-items="[10, 25, 100]"
            :no-data-text="$t('captainProfilePage.welcomeBonus.noMovements')"
            class="elevation-0"
          >
            <template v-slot:items="props">
              <tr>
                <td>{{ formatDateTime(props.item.created_at) }}</td>
                <td>{{ kindLabel(props.item.kind) }}</td>
                <td>{{ formatMoney(props.item.amount) }}</td>
                <td>{{ formatMoney(props.item.bucket_before) }}</td>
                <td>{{ formatMoney(props.item.bucket_after) }}</td>
                <td>
                  <router-link v-if="props.item.booking_id" :to="`/bookings/${props.item.booking_id}`" target="_blank">
                    #{{ props.item.booking_id }}
                  </router-link>
                  <span v-else>-</span>
                </td>
                <td class="wb-reason">{{ props.item.reason || "-" }}</td>
                <td>{{ props.item.created_by_name || $t("finance.common.performedBySystem") }}</td>
                <td>{{ props.item.approved_by_name || "-" }}</td>
              </tr>
            </template>
          </v-data-table>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </template>

    <!-- No summary in the payload (a grant the backfill has not brought into
         the restricted bucket yet, or an older backend): the frozen grant as
         the profile always showed it. -->
    <template v-else>
      <div style="font-weight: bold">
        {{ $t("captainProfilePage.profile.welcomeBonusLabel") }}:
        {{ formatMoney(grantedAmount) }}
        <span v-if="legacyStatus === 'active'" style="color: green">
          ({{ $t("captainProfilePage.profile.welcomeBonusStatusActive") }})
        </span>
      </div>
      <div v-if="grantedAt" style="color: #8599a3">
        {{ $t("captainProfilePage.profile.welcomeBonusGrantedAt") }}:
        {{ formatDay(grantedAt) }}
      </div>
      <div style="color: #8599a3; font-size: 12px">
        {{ $t("captainProfilePage.profile.welcomeBonusLocked") }}
      </div>
      <div class="wb-note">{{ $t("captainProfilePage.welcomeBonus.restrictedNote") }}</div>
    </template>

    <v-dialog v-model="closeDialog" persistent max-width="560px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t("captainProfilePage.welcomeBonus.closeTitle") }}</span>
        </v-card-title>
        <v-card-text>
          <p>{{ $t("captainProfilePage.welcomeBonus.closeIntro", { amount: formatMoney(remaining) }) }}</p>
          <p class="wb-note">{{ $t("captainProfilePage.welcomeBonus.closeApprovalNote") }}</p>
          <v-form ref="closeForm" v-model="closeFormValid" @submit.prevent>
            <v-radio-group v-model="closeForm.kind" :label="$t('captainProfilePage.welcomeBonus.kindLabel')"
              :rules="kindRules" row>
              <v-radio v-for="option in kindOptions" :key="option.value" :label="option.label"
                :value="option.value"></v-radio>
            </v-radio-group>
            <v-textarea v-model="closeForm.reason" :label="$t('captainProfilePage.welcomeBonus.reason')"
              :rules="reasonRules" :counter="REASON_MAX" :maxlength="REASON_MAX" rows="3" auto-grow
              outline></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat :disabled="closing" @click="closeDialog = false">
            {{ $t("captainProfilePage.common.cancel") }}
          </v-btn>
          <v-btn color="error" :loading="closing" :disabled="!closeFormValid" @click="submitClose">
            {{ $t("captainProfilePage.welcomeBonus.closeSubmit") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { formatCompanyTime } from "@/util/companyTime";

// Backend validation of the closure reason: required|string|between:3,500.
const REASON_MIN = 3;
const REASON_MAX = 500;

const STATUS_STYLES = Object.freeze({
  active: { key: "statusActive", color: "green" },
  exhausted: { key: "statusExhausted", color: "blue-grey" },
  expired: { key: "statusExpired", color: "orange darken-2" },
  revoked: { key: "statusRevoked", color: "red" },
});

const KIND_KEYS = Object.freeze({
  grant: "kindGrant",
  consume: "kindConsume",
  expire: "kindExpire",
  revoke: "kindRevoke",
});

export default {
  name: "WelcomeBonusCard",
  props: {
    captainId: {
      type: Number,
      required: true,
    },
    // The backend WB summary ({granted, consumed, remaining, expires_at,
    // closed_at, status, pending_close}); null/absent = no restricted bucket.
    summary: {
      type: Object,
      default: null,
    },
    // Frozen grant columns of the captain row (legacy display).
    grantedAmount: {
      type: [Number, String],
      default: null,
    },
    grantedAt: {
      type: String,
      default: null,
    },
    legacyStatus: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      REASON_MAX,
      movementsPanel: null,
      movements: [],
      movementsLoaded: false,
      movementsLoading: false,
      closeDialog: false,
      closeFormValid: false,
      closeForm: {
        kind: "revoke",
        reason: "",
      },
      closing: false,
      approving: false,
      cancelling: false,
    };
  },
  computed: {
    canManage() {
      return this.hasPermission("WELCOME_BONUS_MANAGE");
    },
    canApprove() {
      return this.hasPermission("WELCOME_BONUS_APPROVE");
    },
    pending() {
      return (this.summary && this.summary.pending_close) || null;
    },
    remaining() {
      const value = Number(this.summary && this.summary.remaining);
      return isNaN(value) ? 0 : value;
    },
    canClose() {
      return (
        this.canManage &&
        !!this.summary &&
        this.remaining > 0 &&
        !this.pending &&
        !this.summary.closed_at
      );
    },
    canCancel() {
      return !!this.pending && (this.canManage || this.canApprove);
    },
    busy() {
      return this.closing || this.approving || this.cancelling;
    },
    statusLabel() {
      const status = this.summary && this.summary.status;
      const style = STATUS_STYLES[status];
      return style ? this.$t(`captainProfilePage.welcomeBonus.${style.key}`) : status || "-";
    },
    statusColor() {
      const style = STATUS_STYLES[this.summary && this.summary.status];
      return style ? style.color : "grey";
    },
    kindOptions() {
      return [
        { value: "revoke", label: this.$t("captainProfilePage.welcomeBonus.kindRevoke") },
        { value: "expire", label: this.$t("captainProfilePage.welcomeBonus.kindExpire") },
      ];
    },
    kindRules() {
      return [(v) => v === "revoke" || v === "expire" || this.$t("captainProfilePage.welcomeBonus.kindRule")];
    },
    reasonRules() {
      return [
        (v) => {
          const length = String(v || "").trim().length;
          return (length >= REASON_MIN && length <= REASON_MAX) || this.$t("captainProfilePage.welcomeBonus.reasonRule");
        },
      ];
    },
    movementHeaders() {
      return [
        { text: this.$t("captainProfilePage.common.date"), value: "created_at", sortable: false },
        { text: this.$t("captainProfilePage.welcomeBonus.movement"), value: "kind", sortable: false },
        { text: this.$t("captainProfilePage.welcomeBonus.amount"), value: "amount", sortable: false },
        { text: this.$t("captainProfilePage.welcomeBonus.remainingBefore"), value: "bucket_before", sortable: false },
        { text: this.$t("captainProfilePage.welcomeBonus.remainingAfter"), value: "bucket_after", sortable: false },
        { text: this.$t("captainProfilePage.common.booking"), value: "booking_id", sortable: false },
        { text: this.$t("captainProfilePage.welcomeBonus.reason"), value: "reason", sortable: false },
        { text: this.$t("captainProfilePage.welcomeBonus.by"), value: "created_by_name", sortable: false },
        { text: this.$t("captainProfilePage.welcomeBonus.approvedBy"), value: "approved_by_name", sortable: false },
      ];
    },
  },
  watch: {
    movementsPanel(value) {
      if (value === 0 && !this.movementsLoaded && !this.movementsLoading) {
        this.loadMovements(true);
      }
    },
  },
  methods: {
    // Final-version doc 2026-10-05: company clock (UTC+3), not the PC's zone.
    formatDay(value) {
      return formatCompanyTime(value, "YYYY-MM-DD");
    },
    formatDateTime(value) {
      return formatCompanyTime(value, "YYYY-MM-DD HH:mm");
    },
    kindLabel(kind) {
      return KIND_KEYS[kind] ? this.$t(`captainProfilePage.welcomeBonus.${KIND_KEYS[kind]}`) : kind || "-";
    },
    // The backend answers business refusals (self-approval, a closure already
    // pending, nothing left to close) with HTTP 400 and a translated message;
    // a missing permission is the middleware's English 403.
    errorMessage(err, fallbackKey) {
      const response = err && err.response;
      const status = response && response.status;
      if (status === 403) {
        return this.$t("captainProfilePage.welcomeBonus.noPermission");
      }
      const message = response && response.data && response.data.message;
      if (message && (status === 400 || status === 409 || status === 422)) {
        return message;
      }
      return this.$t(`captainProfilePage.welcomeBonus.${fallbackKey}`);
    },
    // GET captain/{id}/welcome-bonus -> {summary, movements}: refreshes the
    // movements and hands the fresh summary to the profile.
    loadMovements(notifyOnError) {
      this.movementsLoading = true;
      return this.axios
        .get(this.api.ACTIONS.CAPTAIN_WELCOME_BONUS(this.captainId))
        .then(({ data }) => {
          const payload = (data && data.data) || {};
          this.movements = Array.isArray(payload.movements) ? payload.movements : [];
          this.movementsLoaded = true;
          if (payload.summary !== undefined) {
            this.$emit("summary", payload.summary);
          }
        })
        .catch((err) => {
          if (notifyOnError) {
            window.getApp.$emit("SHOW_TOAST", this.errorMessage(err, "movementsFailed"), "red");
          }
        })
        .finally(() => {
          this.movementsLoading = false;
        });
    },
    // Every action answers {status, summary}; keep the card and an already
    // opened audit trail in step with the server.
    applyResult(result) {
      if (result && result.summary !== undefined) {
        this.$emit("summary", result.summary);
      }
      if (this.movementsLoaded) {
        this.loadMovements(false);
      }
    },
    openCloseDialog() {
      // Past its date but not yet closed by the daily job: expiry is the
      // likely intent; otherwise a manual closure is a revocation.
      const expired = this.summary && this.summary.status === "expired";
      this.closeForm = { kind: expired ? "expire" : "revoke", reason: "" };
      this.closeDialog = true;
      this.$nextTick(() => {
        if (this.$refs.closeForm) this.$refs.closeForm.resetValidation();
      });
    },
    submitClose() {
      if (!this.$refs.closeForm || !this.$refs.closeForm.validate()) {
        return;
      }
      this.closing = true;
      this.axios
        .post(this.api.ACTIONS.CAPTAIN_WELCOME_BONUS_CLOSE(this.captainId), {
          kind: this.closeForm.kind,
          reason: String(this.closeForm.reason || "").trim(),
        })
        .then(({ data }) => {
          const result = (data && data.data) || {};
          this.closeDialog = false;
          window.getApp.$emit(
            "SHOW_TOAST",
            this.$t(
              result.status === "pending_approval"
                ? "captainProfilePage.welcomeBonus.closePending"
                : "captainProfilePage.welcomeBonus.closed"
            ),
            "green"
          );
          this.applyResult(result);
        })
        .catch((err) => {
          window.getApp.$emit("SHOW_TOAST", this.errorMessage(err, "closeFailed"), "red");
          // Someone else may have closed or requested it meanwhile.
          this.loadMovements(false);
        })
        .finally(() => {
          this.closing = false;
        });
    },
    approveClose() {
      if (!this.pending) return;
      // The approval closes what is left NOW (rides may have used part of it
      // since the request), so the confirmation names the current remainder.
      this.$dialova({
        title: this.$t("captainProfilePage.welcomeBonus.approveTitle"),
        paragraph: this.$t("captainProfilePage.welcomeBonus.approveConfirm", {
          amount: this.formatMoney(this.remaining),
        }),
        loader: true,
      })
        .then(({ close }) => {
          this.approving = true;
          return this.axios
            .post(this.api.ACTIONS.CAPTAIN_WELCOME_BONUS_CLOSE_APPROVE(this.captainId))
            .then(({ data }) => {
              window.getApp.$emit("SHOW_TOAST", this.$t("captainProfilePage.welcomeBonus.approved"), "green");
              this.applyResult((data && data.data) || {});
            })
            .catch((err) => {
              window.getApp.$emit("SHOW_TOAST", this.errorMessage(err, "approveFailed"), "red");
              this.loadMovements(false);
            })
            .finally(() => {
              this.approving = false;
              close();
            });
        })
        .catch(() => {
          // Dialog dismissed: nothing was sent.
        });
    },
    cancelClose() {
      if (!this.pending) return;
      this.$dialova({
        title: this.$t("captainProfilePage.welcomeBonus.cancelTitle"),
        paragraph: this.$t("captainProfilePage.welcomeBonus.cancelConfirm"),
        loader: true,
      })
        .then(({ close }) => {
          this.cancelling = true;
          return this.axios
            .post(this.api.ACTIONS.CAPTAIN_WELCOME_BONUS_CLOSE_CANCEL(this.captainId))
            .then(({ data }) => {
              window.getApp.$emit("SHOW_TOAST", this.$t("captainProfilePage.welcomeBonus.cancelled"), "green");
              this.applyResult((data && data.data) || {});
            })
            .catch((err) => {
              window.getApp.$emit("SHOW_TOAST", this.errorMessage(err, "cancelFailed"), "red");
              this.loadMovements(false);
            })
            .finally(() => {
              this.cancelling = false;
              close();
            });
        })
        .catch(() => {
          // Dialog dismissed: nothing was sent.
        });
    },
  },
};
</script>

<style scoped>
.wb-card {
  border-top: 1px solid #d5dde1;
  padding: 12px 20px 16px;
}

.wb-head {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.wb-title {
  font-size: 16px;
  font-weight: bold;
}

.wb-figures {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 32px;
  margin-top: 8px;
}

.wb-figure {
  display: flex;
  flex-direction: column;
  min-width: 110px;
}

.wb-label {
  color: #8599a3;
  font-size: 12px;
}

.wb-value {
  font-weight: bold;
}

.wb-value.remaining {
  color: #b26500;
}

.wb-note {
  color: #8599a3;
  font-size: 12px;
  margin-top: 6px;
}

.wb-pending {
  background-color: rgba(252, 179, 45, 0.12);
  border: 1px solid rgba(252, 179, 45, 0.6);
  border-radius: 6px;
  margin-top: 10px;
  padding: 10px 12px;
}

.wb-pending-title {
  font-weight: bold;
  margin-bottom: 4px;
}

.wb-pending-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}

.wb-reason {
  max-width: 320px;
  overflow-wrap: anywhere;
  white-space: pre-line;
}

.wb-pending .wb-reason {
  max-width: none;
}

.wb-movements {
  box-shadow: none;
  margin-top: 10px;
}
</style>
